import React, { useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

// pdfjs (v3)
// 워커를 side effect import 하면 안 된다. 그 import 는 v3 워커를 globalThis.pdfjsWorker
// 에 등록하는데, 이 전역은 앱 전체가 공유한다. react-pdf 가 쓰는 pdfjs v5 는 저 전역이
// 있으면 자기 워커를 만들지 않고 그걸 그대로 써서, 제안서 «조회» 가
// "API version 5.4.296 does not match Worker version 3.11.174" 로 죽는다.
import * as pdfjsLib from "pdfjs-dist/build/pdf";

// MUI
import {
    Box,
    Paper,
    Stack,
    Typography,
    TextField,
    Button,
    Backdrop,
    CircularProgress,
    Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LoadingDots from "../../../component/common/LoadingDots";
import ActionButton from "../../../component/common/ActionButton";
import ComboBox from "../../../component/common/ComboBox";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { proposalApi } from "./api/proposalApi";

// 워커는 cdnjs 에서 직접 받는다. 번들 경로(new URL(..., import.meta.url))로 바꾸면
// CRA 가 워커를 babel 로 한 번 돌리면서 @babel/runtime 헬퍼 import 를 빌드 머신의
// 로컬 절대경로로 심어 배포본에서 404 가 나고, nginx 도 .mjs 를 application/octet-stream
// 으로 내려 모듈 워커 자체가 거부된다.
pdfjsLib.GlobalWorkerOptions.workerSrc =
    "//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

export default function ProposalUpload() {
    const history = useHistory();
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const category = query.get("category") || "";

    const [form, setForm] = useState({
        subCategory: "",
        year: new Date().getFullYear(),
        name: "",
        tags: "",
    });

    const [selectedFile, setSelectedFile] = useState(null); // File
    const [pdfContents, setPdfContents] = useState(""); // 전체 텍스트
    const [pages, setPages] = useState([]); // [{page, contents}]
    const [isSubmitting, setIsSubmitting] = useState(false);
    // PDF 텍스트 추출은 파일 크기에 비례해 수 초가 걸린다. selectedFile 은 파일 선택 즉시 세팅되는데
    // pdfContents 는 await 이후에야 채워지므로, 그 사이 등록이 눌리면 contents 가 빈 문자열로 전송된다.
    // 서버의 @RequestPart("contents") 는 '빈 파트'와 '없는 파트'를 구분하지 않아 500(파트 누락)이 난다.
    const [isExtracting, setIsExtracting] = useState(false);

    // 파일 재선택·등록만 막는다. 분류/년도/행사명/태그는 추출 중에도 입력할 수 있어야
    // 수 초의 대기가 낭비되지 않는다.
    const busy = isSubmitting || isExtracting;

    // 추출이 끝났는데 텍스트가 없다 = 추출 실패이거나 텍스트 레이어가 없는 스캔본.
    // 이 상태로 보내면 서버가 400(필수 항목 비어 있음)으로 거절하므로 버튼 단계에서 막는다.
    const extractFailed = !isExtracting && Boolean(selectedFile) && !pdfContents.trim();


    const fileInputRef = useRef(null);

    const years = useMemo(() => {
        const now = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => now - i);
    }, []);

    // PDF 텍스트 추출
    const extractTextFromPDF = async (file) => {
        const buf = await file.arrayBuffer();
        const typedArray = new Uint8Array(buf);

        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;

        const tempPages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const strings = content.items.map((it) => it.str);
            const text = strings.join(" ");
            tempPages.push({ page: i, contents: text });
        }

        const allText = tempPages.map((p) => p.contents).join("\n");
        return { allText, tempPages, numPages: pdf.numPages };
    };

    const handlePickFile = () => {
        if (busy) return;
        fileInputRef.current?.click();
    };

    const handleFile = async (file) => {
        if (busy) return;
        if (!file) return;

        // PDF만 허용
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            notify("PDF 파일만 업로드할 수 있습니다.", "error");
            return;
        }

        setSelectedFile(file);
        // 이전 파일의 추출 결과가 남아 새 PDF 의 내용으로 저장되는 것을 막는다.
        setPdfContents("");
        setPages([]);
        setIsExtracting(true);

        try {
            notify("PDF 텍스트 추출 중...", "info");
            const { allText, tempPages, numPages } = await extractTextFromPDF(file);
            setPdfContents(allText);
            setPages(tempPages);
            notify(`${file.name} 텍스트 추출 완료 (총 ${numPages}페이지)`, "success");
        } catch (err) {
            console.error(err);
            notify(`PDF 텍스트 추출 실패: ${err?.message || "알 수 없는 오류"}`, "error");
            setPdfContents("");
            setPages([]);
        } finally {
            setIsExtracting(false);
        }
    };

    // Drag & Drop
    const onDragOver = (e) => {
        if (busy) return;
        e.preventDefault();
        e.stopPropagation();
    };
    const onDrop = (e) => {
        if (busy) return;
        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files?.[0];
        handleFile(file);
    };

    const setValue = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async () => {
        if (isSubmitting) return;
        // 버튼은 비활성이지만 Enter 등 다른 경로로 들어올 수 있어 여기서도 막는다.
        if (isExtracting) return notify("PDF 텍스트를 추출하는 중입니다. 완료 후 등록해주세요.", "warning");
        // 기본 검증
        if (!form.subCategory) return notify("분류를 선택하세요.", "warning");
        if (!form.year) return notify("년도를 선택하세요.", "warning");
        if (!form.name?.trim()) return notify("행사명을 입력하세요.", "warning");
        if (!selectedFile) return notify("PDF 파일을 업로드하세요.", "warning");
        // contents 가 비면 서버가 '파트 누락'으로 판정해 500 이 난다. 여기서 미리 막고 원인을 알려준다.
        if (!pdfContents.trim()) return notify("PDF 텍스트를 추출하지 못했습니다. 파일을 다시 선택해주세요.", "warning");

        const tagsArr = form.tags
            ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
            : [];

        const formData = new FormData();
        formData.append("subCategory", form.subCategory || "");
        formData.append("year", String(form.year || ""));
        formData.append("name", form.name || "");
        formData.append("tags", JSON.stringify(tagsArr));
        formData.append("pages", JSON.stringify(pages || []));
        formData.append("contents", pdfContents || "");

        formData.append("file", selectedFile);

        await runApiAction({
            request: () => proposalApi.upload(formData),
            errorMsg: serverMsgOr("업로드 실패"),
            successMsg: "등록되었습니다.",
            validate: (data) => data?.id && data?.path ? true : "제안서 파일 저장 결과가 비어 있습니다.",
            setLoading: setIsSubmitting,
            onSuccess: () => history.push(`/onandme/proposal?category=${encodeURIComponent(category)}`),
            onError: (error) => console.error(error),
        });
    };

    const fileLabel = selectedFile?.name || "PDF 파일을 선택하세요";

    return (
        <Box sx={{ maxWidth: 720, mx: "auto", py: 4 }}>
            {/* 로딩 오버레이 */}
            <Backdrop
                open={isSubmitting}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 10,
                    bgcolor: "rgba(255,255,255,0.85)",
                }}
            >
                <LoadingDots label="처리하고 있어요" />
            </Backdrop>

            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                <Stack spacing={2.5}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        제안서 업로드
                    </Typography>

                    <Divider />

                    {/* 분류 */}
                    <ComboBox clearable={false} placeholder="분류" disabled={isSubmitting}
                        options={[
                            { value: "MICE", label: "MICE" },
                            { value: "VIDEO_PLANNING", label: "영상기획" },
                        ]}
                        value={form.subCategory}
                        onChange={(v) => setValue("subCategory", v)} />

                    {/* 년도 */}
                    <ComboBox clearable={false} placeholder="년도" disabled={isSubmitting}
                        options={years.map((y) => ({ value: y, label: String(y) }))}
                        value={form.year}
                        onChange={(v) => setValue("year", v)} />

                    {/* 행사명 */}
                    <TextField
                        label="행사명"
                        value={form.name}
                        onChange={(e) => setValue("name", e.target.value)}
                        fullWidth
                        disabled={isSubmitting}
                    />

                    {/* 태그 */}
                    <TextField
                        label="태그"
                        placeholder="쉼표로 구분 (예: TEL, KERIS, 행사)"
                        value={form.tags}
                        onChange={(e) => setValue("tags", e.target.value)}
                        fullWidth
                        disabled={isSubmitting}
                    />

                    {/* PDF 업로드 영역 */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            PDF 업로드 (1개)
                        </Typography>

                        <Box
                            onDragOver={onDragOver}
                            onDrop={onDrop}
                            onClick={handlePickFile}
                            sx={{
                                cursor: busy ? "not-allowed" : "pointer",
                                border: "1px dashed",
                                borderColor: "divider",
                                borderRadius: 1.5,
                                p: 3,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                bgcolor: "background.paper",
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <PictureAsPdfIcon fontSize="large" />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 700 }}>
                                    PDF 파일을 드래그하거나 클릭하세요
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    PDF 텍스트 추출 후 업로드에 포함됩니다.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    선택됨: <b>{fileLabel}</b>
                                </Typography>
                            </Box>

                            <ActionButton variant="secondary"
                                startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                                disabled={busy}
                                label="파일 선택" />

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,application/pdf"
                                style={{ display: "none" }}
                                disabled={busy}
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                        </Box>

                        {/* 추출 결과 간단 표시 */}
                        <Typography variant="caption" sx={{ mt: 1, display: "block" }}
                            color={extractFailed ? "error.main" : "text.secondary"}>
                            {isExtracting
                                ? "PDF 텍스트를 추출하고 있습니다. 완료되면 등록할 수 있습니다."
                                : extractFailed
                                    ? "PDF 텍스트를 추출하지 못했습니다. 파일을 다시 선택해주세요."
                                    : `추출 페이지 수: ${pages?.length || 0} / 전체 텍스트 길이: ${pdfContents?.length || 0}`}
                        </Typography>
                    </Box>

                    {/* 등록 버튼 */}
                    <ActionButton
                        variant="primary"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={busy || extractFailed}
                        label={isExtracting
                            ? <LoadingDots compact label="글자를 추출하고 있어요"
                                sx={{ py: 0, px: 0, color: "inherit" }} />
                            : "등록"}
                        sx={{ height: 52 }}
                        startIcon={false}
                    />
                </Stack>
            </Paper>

        </Box>
    );
}
